import { useQuery } from '@tanstack/react-query';
import { CornerDownRight, MessageSquare } from 'lucide-react';
import type { CommentItem, CommentStatus } from '@news-cms/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { commentThreadQueryOptions } from '@/lib/comments';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When non-null, fetch + show the thread anchored at this comment. */
  commentId: string | null;
}

const STATUS_TINT: Record<CommentStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  spam: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const STATUS_LABEL: Record<CommentStatus, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติ',
  rejected: 'ปฏิเสธ',
  spam: 'สแปม',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Shows a comment in context: the parent (if any), the comment
 * itself, and its direct replies. Lets moderators see what a
 * comment was responding to before deciding to approve / reject —
 * the most-common piece of missing context when reviewing reports.
 *
 * Single-level depth (parent + target + direct replies). Deeper
 * trees would need a recursive walk; news comments are usually 1-
 * deep in practice.
 */
export function CommentThreadDialog({ open, onOpenChange, commentId }: Props) {
  const query = useQuery(commentThreadQueryOptions(commentId));
  const data = query.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-slate-400" />
            ดูเป็นบทสนทนา
          </DialogTitle>
          <DialogDescription>
            {data ? `บน: ${data.target.postTitle}` : 'กำลังโหลดบริบท...'}
          </DialogDescription>
        </DialogHeader>

        {query.isLoading && (
          <div className="py-8 text-center text-sm text-slate-500">กำลังโหลด...</div>
        )}
        {query.isError && (
          <div className="py-8 text-center text-sm text-red-600">โหลดบทสนทนาไม่สำเร็จ</div>
        )}
        {data && (
          <div className="space-y-3">
            {data.parent && <CommentRow comment={data.parent} dim />}
            <div
              className={
                data.parent ? 'border-l-2 border-slate-200 pl-4 dark:border-slate-700' : ''
              }
            >
              <CommentRow comment={data.target} highlight />
            </div>
            {data.replies.length > 0 && (
              <div className="ml-7 space-y-2 border-l border-dashed border-slate-200 pl-4 dark:border-slate-700">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400">
                  <CornerDownRight className="h-3 w-3" />
                  {data.replies.length} ตอบกลับ
                </div>
                {data.replies.map((r) => (
                  <CommentRow key={r.id} comment={r} />
                ))}
              </div>
            )}
            {data.replies.length === 0 && !data.parent && (
              <p className="text-xs text-slate-400">
                คอมเมนต์นี้ยังไม่มีคนตอบ และไม่ได้ตอบ comment ไหน
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CommentRow({
  comment,
  highlight,
  dim,
}: {
  comment: CommentItem;
  highlight?: boolean;
  dim?: boolean;
}) {
  return (
    <div
      className={
        highlight
          ? 'border-brand-300 bg-brand-50/40 dark:border-brand-700 dark:bg-brand-900/20 rounded-md border-2 p-3'
          : dim
            ? 'rounded-md border border-slate-200 bg-slate-50 p-3 opacity-80 dark:border-slate-800 dark:bg-slate-800/40'
            : 'rounded-md border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900'
      }
    >
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
          {comment.authorName}
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${STATUS_TINT[comment.status]}`}>
            {STATUS_LABEL[comment.status]}
          </span>
        </div>
        <span className="shrink-0 text-[11px] text-slate-400">
          {formatDateTime(comment.createdAt)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
        {comment.body}
      </p>
    </div>
  );
}
